import { useContext, useEffect, useMemo, useState } from 'react'

//context
import { OrgSettingsContext } from '@/context/OrgSettings';
import { OrgSettingsUsersContext } from '@/context/OrgSettingsUsersContext';

//api
import { addWhitelistIpAddress, getPasskeySettingsForOrg, listPasskeysForUser, listRecoveryCodesForUser, listRecoveryRequestsForUser, listWhitelistAddressesForUser, removeWhitelistIpAddress, revokeAccountRecoveryCodes, setUserRecoveryIPRestriction } from '@/api/orgSettings/passkeys';

//interfaces
import { NotificationStatus } from  '@/interfaces/languages';
import { ISimpleTableProps } from 'thrive-components';
import { IPasskey, IRecoveryCode, IWhitelistData } from  '@/interfaces/passkeys';

//helpers
import { formatPasskeyData, formatRecoveryData, formatWhitelistData, passkeyTableColumns, recoveryCodesTableColumns, whitelistTableColumns } from './UserPasskeyInformationFormatters';
import { useTimer } from  '@/components/hooks/utility/useTimer';




const UseUserPasskeyInformation = () => {  
    const {userId} = useContext(OrgSettingsUsersContext);
    const {orgId} = useContext(OrgSettingsContext);
    const [passkeyData, setPasskeyData] = useState<IPasskey[]>([]);
    const [recoveryCodeData, setRecoveryCodeData] = useState<IRecoveryCode[]>([]);
    const [showRevokeModal, setShowRevokeModal] = useState<boolean>(false);
    const [whitelistData, setWhitelistData] = useState<IWhitelistData[]>([]);
    const [passkeySkip, setPasskeySkip] = useState<number>(0);
    const [whitelistSkip, setWhitelistSkip] = useState<number>(0);
    const [recoveryCodeSkip, setRecoveryCodeSkip] = useState<number>(0);
    const [removeIpId, setRemoveIpId] = useState<number | null>(null);
    const [showRequestPanel, setShowRequestPanel] = useState<boolean>(false);    
    const [showCreateCodeModal, setShowCreateCodeModal] = useState<boolean>(false);
    const [showRestrictionModal, setShowRestrictionModal] = useState<boolean>(false);
    const [showWhitelistAddressModal, setShowWhitelistAddressModal] = useState<boolean>(false);
    const [showRecoveryRequestsModal, setShowRecoveryRequestsModal] = useState<boolean>(false);
    const [restrictedAccess, setRestrictedAccess] = useState<boolean>(true);
    const [passkeysEnabled, setPasskeysEnabled] = useState<boolean>(false);
    const [setUpCompleted, setSetUpCompleted] = useState<boolean>(false);
    const [notificationDetails, setNotificationDetails] = useState<{show: boolean, status: NotificationStatus | null, message: string}>();
    const [isLoadingPasskeyData, setIsLoadingPasskeyData] = useState<boolean>(true);
    const [isLoadingRecoveryCodeData, setIsLoadingRecoveryCodeData] = useState<boolean>(true);
    const [isLoadingWhitelist, setIsLoadingWhitelist] = useState<boolean>(true);
    const [hasMorePasskeys, setHasMorePasskeys] = useState<boolean>(false);
    const [hasMoreRecoveryCodes, setHasMoreRecoveryCodes] = useState<boolean>(false);
    const [hasMoreWhitelistAddresses, setHasMoreWhitelistAddresses] = useState<boolean>(false);
    const [whitelistIsEnabled, setWhitelistIsEnabled] = useState<boolean>(false);
    const [canCreateRecoveryCode, setCanCreateRecoveryCode] = useState<boolean>(false);
    const [isViewingPersonalAccount, setIsViewingPersonalAccount] = useState<boolean>(true);

    const passkeyTableContent = useMemo<ISimpleTableProps>(() => (
        {
            simpleTableContent: {columns: passkeyTableColumns, rows: formatPasskeyData(passkeyData)},
            isInfiniteScroll: true,
            isLoading: isLoadingPasskeyData,
            noDataMessage: "There are no registered passkeys for this user.",
            getMoreContent: onGetMorePasskeys,
            infiniteScrollFetchCondition: hasMorePasskeys,
            hideActionsColumn: true
        }), [ passkeyData, isLoadingPasskeyData]
    );

    const recoveryCodesTableContent = useMemo<ISimpleTableProps>(() => (
        {
            simpleTableContent: {columns: recoveryCodesTableColumns, rows: formatRecoveryData(recoveryCodeData, () => setShowRevokeModal(true))},
            isInfiniteScroll: true,
            isLoading: isLoadingRecoveryCodeData,
            noDataMessage: "There are no recovery codes generated for this user.",
            getMoreContent: onGetMoreRecoveryCodes,
            infiniteScrollFetchCondition: hasMoreRecoveryCodes,
        }), [ recoveryCodeData, isLoadingRecoveryCodeData]
    );

    const whitelistTableContent = useMemo<ISimpleTableProps>(() => (
        {
            simpleTableContent: {columns: whitelistTableColumns, rows: formatWhitelistData(whitelistData, setRemoveIpId)},
            isInfiniteScroll: true,
            isLoading: isLoadingWhitelist,
            noDataMessage: "No trusted IP Addresses added",
            getMoreContent: onGetMoreWhitelistAddresses,
            infiniteScrollFetchCondition: hasMoreWhitelistAddresses,
        }), [ whitelistData, isLoadingWhitelist]
    );

    useEffect(()=> {
        if (orgId){
            getPasskeySettings();
        }
    }, [orgId]);
    
    useEffect(()=> {
        if (!restrictedAccess && passkeysEnabled) {
            getUserPasskeyData();
            getAccountRecoveryCodeData();
            getWhiteListData();
            setRecoveryRequestState();
        }
    }, [setUpCompleted]);

    useEffect(()=> {
        checkRecoveryCodeStatus();
    }, [recoveryCodeData]);

    useTimer(notificationDetails?.show ?? false, ()=>setNotificationDetails({show: false, status:null, message: ""}), 3000);
    
    function getPasskeySettings() {
        getPasskeySettingsForOrg(orgId)
        .then((data) => {
            setPasskeysEnabled(data.passkeySignInEnabled);
            setRestrictedAccess(false);
        })
        .catch((error: { status: number; }) => {
            if (error.status === 403) {
                setRestrictedAccess(true);
            } else {
                setNotificationDetails({show: true, status: NotificationStatus.error, message: "Something went wrong. Please try again."});
            }
        })
        .finally(() => {
            setSetUpCompleted(true);
        });
    }
    
    function getUserPasskeyData(isPagination?: boolean) {
        let passkeyCount = passkeyData?.length ?? 0;
        listPasskeysForUser(Number(userId), passkeySkip)
        .then(({items, totalItemCount, nextSkip}) => {
            if (isPagination) {
                setPasskeyData((prevDraftData) => [...(prevDraftData || []), ...items]);
            } else {
                setPasskeyData(items);
            } 
            setHasMorePasskeys((passkeyCount + (items?.length ?? 0)) < totalItemCount);
            setPasskeySkip(nextSkip ?? 0);
        })
        .catch(() => {
            setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable to fetch passkey data at this time. Please try again."});
        })
        .finally(() => {
            setIsLoadingPasskeyData(false)
        });
    }

    function getAccountRecoveryCodeData(isPagination?: boolean) {
        let codeCount = recoveryCodeData?.length ?? 0;
        listRecoveryCodesForUser(Number(userId), recoveryCodeSkip)
        .then(({items, totalItemCount, nextSkip}) => {
            if (isPagination) {
                setRecoveryCodeData((prevDraftData) => [...(prevDraftData || []), ...items]);
            } else {
                setRecoveryCodeData(items);
            }
            setHasMoreRecoveryCodes((codeCount + (items?.length ?? 0)) < totalItemCount);
            setRecoveryCodeSkip(nextSkip ?? 0);
            setIsViewingPersonalAccount(false);
        })
        .catch((error) => {
            if (error.status === 403) {
                setIsViewingPersonalAccount(true);
            } else {
                setIsViewingPersonalAccount(false);
                setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable to fetch recovery code data at this time. Please try again."});
            }
        })
        .finally(() => {
            setIsLoadingRecoveryCodeData(false)
        });
    }

    function setRecoveryRequestState() {
        listRecoveryRequestsForUser(Number(userId), "")
        .then((data) => {
            setShowRequestPanel(data?.items.length > 0);
        })
        .catch((error) => {
            if (error.status !== 403) {
                setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable to fetch recovery request data. Please try again"});
            }
        })
    }

    function getWhiteListData(isPagination?: boolean) {
        let whitelistCount = whitelistData?.length ?? 0;
        listWhitelistAddressesForUser(Number(userId), whitelistSkip ?? 0)
        .then(({items, totalItemCount, nextSkip, whitelistEnabled}) => {
            setWhitelistIsEnabled(whitelistEnabled);
            if (whitelistEnabled) {
                if (isPagination) {
                    setWhitelistData((prevDraftData) => [...(prevDraftData || []), ...items]);
                } else {
                    setWhitelistData(items);
                } 
                setHasMoreWhitelistAddresses((whitelistCount + (items?.length ?? 0)) < totalItemCount);
                setWhitelistSkip(nextSkip ?? 0);
            }
        })
        .catch(() => {
            setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable to fetch whitelist address data at this time. Please try again."});
        })
        .finally(() => {
            setIsLoadingWhitelist(false)
        });
    }
    
    function onRevokeCodeConfirmClick() {
        revokeAccountRecoveryCodes(Number(userId))
        .catch(() => {
            setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable to revoke account recovery codes at this time. Please try again."});
        })
        .finally(() => {
            getAccountRecoveryCodeData();
            setShowRevokeModal(false);
        });
    }

    function onAddWhitelistAddress(ipAddress: string) {
        const payload = {
            "ipAddress": ipAddress
        }
        addWhitelistIpAddress(Number(userId), payload)
        .catch((error) => {
            if (error.status === 400) {
                setNotificationDetails({
                    show: true,
                    status: NotificationStatus.error,
                    message: "Unaccepted IP format. Review your IP and try again."
                });
            } else {
                setNotificationDetails({
                    show: true,
                    status: NotificationStatus.error,
                    message: "Unable to update IP address restriction settings at this time. Please try again."
                });
            }        
        })
        .finally(() => {
            getWhiteListData();
        });
    }

    function onRemoveWhitelistAddress(ipAddressId?: number) {
        let id: number = ipAddressId ?? Number(removeIpId) ?? 0;
        removeWhitelistIpAddress(Number(userId), id)
        .catch(() => {
            setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable update IP address restriction settings at this time. Please try again."});
        })
        .finally(() => {
            setRemoveIpId(null);
            getWhiteListData();
        });
    }
    
    function onRestrictWhitelistClick() {
        setShowRestrictionModal(false);
        
        const payload = {
            restrictIp: !whitelistIsEnabled
        }
        setUserRecoveryIPRestriction(Number(userId), payload)
        .then(() => {
            setWhitelistIsEnabled(!whitelistIsEnabled);
        })
        .catch(() => {
            setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable update IP address restriction settings at this time. Please try again."});
        })
    }

    function checkRecoveryCodeStatus() {
        const hasActiveCode = recoveryCodeData.some(code => code.status === "active");
        setCanCreateRecoveryCode(!hasActiveCode);
    }
    
    function onGetMorePasskeys() {
        getUserPasskeyData(true);
    }

    function onGetMoreRecoveryCodes() {
        getAccountRecoveryCodeData(true);
    }

    function onGetMoreWhitelistAddresses() {
        getWhiteListData(true);
    }

    function refreshRecoveryCodes() {
        getAccountRecoveryCodeData();
    }
    return {
        restrictedAccess,
        setUpCompleted,
        notificationDetails,
        whitelistIsEnabled,
        whitelistTableContent,
        canCreateRecoveryCode,
        passkeyTableContent,
        recoveryCodesTableContent,
        showRequestPanel,
        showRecoveryRequestsModal,
        showCreateCodeModal,
        showRevokeModal,
        removeIpId,
        showRestrictionModal,
        showWhitelistAddressModal,
        whitelistData,
        passkeysEnabled,
        isViewingPersonalAccount,
        refreshRecoveryCodes,
        setShowWhitelistAddressModal,
        setShowRestrictionModal,
        onRestrictWhitelistClick,
        setRemoveIpId, 
        setShowRevokeModal,
        setShowCreateCodeModal,
        setShowRecoveryRequestsModal,
        onAddWhitelistAddress,
        onRemoveWhitelistAddress,
        onRevokeCodeConfirmClick
    }
}

export default UseUserPasskeyInformation